"use client";

import CaretRegularDownIcon from "public/next-public/common/icons/caret-regular-down.svg";
import CaretDownIcon from "public/next-public/common/icons/caret-solid-down.svg";
import ErrorIcon from "public/next-public/common/icons/error-icon.svg";
import React from "react";
import styles from "./dropdown.module.scss";

export type DropdownSize = "default" | "compact";

interface DropdownProps {
  id?: string;
  name?: string;
  label?: string;
  value?: string | number;
  /**
   * "compact" is the narrow inline quantity select (62px wide, centered value,
   * no visible label) — pair it with `ariaLabel`. Defaults to "default".
   */
  size?: DropdownSize;
  /** Accessible name when no visible `label` is rendered. */
  ariaLabel?: string;
  required?: boolean;
  optional?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  children: React.ReactNode;
  "aria-describedby"?: string;
  /**
   * Forwarded to the underlying `<select>`. Consumers that defer building a long
   * option list until interaction (see useLazySelectOptions) hook these.
   */
  onFocus?: React.FocusEventHandler<HTMLSelectElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLSelectElement>;
  onTouchStart?: React.TouchEventHandler<HTMLSelectElement>;
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  name,
  label,
  value,
  size = "default",
  ariaLabel,
  required,
  optional,
  disabled,
  error,
  errorMessage,
  placeholder,
  className,
  labelClassName,
  onChange,
  children,
  "aria-describedby": ariaDescribedBy,
  onFocus,
  onMouseEnter,
  onTouchStart,
}) => {
  const selectClasses = [styles.select, error ? styles.error : "", disabled ? styles.disabled : "", className || ""]
    .filter(Boolean)
    .join(" ");

  const errorMessageId = id && errorMessage ? `${id}-error` : undefined;
  const mergedAriaDescribedBy = [ariaDescribedBy, errorMessageId].filter(Boolean).join(" ") || undefined;

  const isCompact = size === "compact";

  return (
    <div className={`${styles.Dropdown} ${isCompact ? styles.compact : ""}`.trim()}>
      {label && (
        <label className={[styles.label, labelClassName].filter(Boolean).join(" ")} htmlFor={id}>
          {label}
          {required && <span className={styles.required}>*</span>}
          {optional && !required && <span className={styles.optional}>(Optional)</span>}
        </label>
      )}
      <div className={styles.selectWrapper}>
        <select
          id={id}
          name={name}
          value={value}
          required={required}
          disabled={disabled}
          className={selectClasses}
          onChange={onChange}
          onFocus={onFocus}
          onMouseEnter={onMouseEnter}
          onTouchStart={onTouchStart}
          aria-invalid={error || undefined}
          aria-describedby={mergedAriaDescribedBy}
          aria-label={!label ? (ariaLabel ?? placeholder) : undefined}
        >
          {children}
        </select>
        <span className={styles.caret} aria-hidden="true">
          {isCompact ? <CaretRegularDownIcon width={12} height={12} /> : <CaretDownIcon width={12} height={12} />}
        </span>
      </div>
      {errorMessage && (
        <div
          id={errorMessageId}
          role={error ? "alert" : undefined}
          className={`${styles.errorMessage} ${error ? "" : styles.invalidOnly}`}
        >
          <span className={styles.errorIcon}>
            <ErrorIcon width={12} height={12} aria-hidden />
          </span>
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
